import React, { forwardRef, useContext } from 'react'
import { css, cx } from '@linaria/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useTheme } from '../theme'
import colorThemes from '../theme/themeSubsets'
import { WhiteThemeContext } from '../theme/white-theme'
import type { BaseButtonProps } from '../buttons/types'
import BaseChip, { chipCss } from './BaseChip'
import { colorCss, lowChipCss, highChipCss } from './colorStyles'
import BaseButton from '../buttons/BaseButton'

export const contentCss = css`
  cursor: pointer;
  height: 32px;
  padding: 8px 11px;

  &[data-inline] {
    height: 20px;
    padding: 2px 8px 3px;
  }
`

export type ChipProps = BaseButtonProps & {
  centered?: boolean
  color?: 'gray' | 'blue' | 'yellow' | 'red' | 'green' | 'purple'
  emphasis?: 'low' | 'high'
  inline?: boolean
  role?: string
  selected?: boolean
  iconAtEnd?: boolean
}

const Chip = forwardRef<typeof BaseButton, ChipProps>(
  (
    {
      centered = false,
      children,
      className,
      color = 'gray',
      disabled,
      emphasis = 'high',
      inline,
      onClick,
      // @ts-expect-error fixme: remove in a future release
      emphasized,
      selected,
      to,
      ...props
    }: ChipProps,
    ref
  ): JSX.Element => {
    const isWhite = useContext(WhiteThemeContext)
    const isNight = useTheme() === 'night'
    const colorTheme = colorThemes[color]
    return (
      <BaseChip
        as={BaseButton}
        {...props}
        tabIndex={(onClick || to) && !disabled ? 0 : -1}
        aria-disabled={Boolean(disabled)}
        className={cx(chipCss, colorTheme, className, colorCss, emphasis === 'low' ? lowChipCss : highChipCss)}
        data-centered={boolAttr(centered)}
        data-inline={boolAttr(inline)}
        data-selected={boolAttr(selected || emphasized)}
        data-night={boolAttr(isNight)}
        data-white={boolAttr(isWhite)}
        data-gray-chip={boolAttr(color === 'gray')} // This helps in WhiteTheme when it goes white-on-white
        disabled={disabled}
        onClick={onClick}
        ref={ref}
        to={to}
      >
        {children}
      </BaseChip>
    )
  }
)

export default Chip
